import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from "react-router-dom";
import { SpriteFourStep, SpriteFrame } from './Experimental';
import { getGameLeaderboard, getGameScore, saveGameScore } from '../api';
import redSprite from '../images/bulba/characters/Red.png';
import keyItemSfx from '../images/bulba/games/08ObtainedAKeyItem.mp3';
import noGoodSfx from '../images/bulba/games/21PokédexEvaluationNoGood.mp3';
import levelUpSfx from '../images/bulba/games/12LevelUp.mp3';
import battleBgm from '../images/bulba/games/43BattleGymLeaderJohtoVersion.mp3';
import grassFlat        from '../images/bulba/textures/grass2.png';
import ogCenter         from '../images/bulba/textures/grass2.png';
import ogTop            from '../images/bulba/textures/grassTop.png';
import ogBottom         from '../images/bulba/textures/grassBottom.png';
import ogLeft           from '../images/bulba/textures/grassLeft.png';
import ogRight          from '../images/bulba/textures/grassRight.png';
import ogTopLeft        from '../images/bulba/textures/grassTopLeft.png';
import ogTopRight       from '../images/bulba/textures/grassTopRight.png';
import ogBottomLeft     from '../images/bulba/textures/grassBottomLeft.png';
import ogBottomRight    from '../images/bulba/textures/grassBottomRight.png';
import lgTopLeft     from '../images/bulba/textures/LightGrass/sprite-597-1.png';
import lgTop         from '../images/bulba/textures/LightGrass/sprite-597-2.png';
import lgTopRight    from '../images/bulba/textures/LightGrass/sprite-597-3.png';
import lgLeft        from '../images/bulba/textures/LightGrass/sprite-598-1.png';
import lgCenter      from '../images/bulba/textures/LightGrass/sprite-598-2.png';
import lgRight       from '../images/bulba/textures/LightGrass/sprite-598-3.png';
import lgBottomLeft  from '../images/bulba/textures/LightGrass/sprite-599-1.png';
import lgBottom      from '../images/bulba/textures/LightGrass/sprite-599-2.png';
import lgBottomRight from '../images/bulba/textures/LightGrass/sprite-599-3.png';

import dgTopLeft     from '../images/bulba/textures/DarkGrass/sprite-607-4.png';
import dgTop         from '../images/bulba/textures/DarkGrass/sprite-607-5.png';
import dgTopRight    from '../images/bulba/textures/DarkGrass/sprite-607-6.png';
import dgLeft        from '../images/bulba/textures/DarkGrass/sprite-608-4.png';
import dgCenter      from '../images/bulba/textures/DarkGrass/sprite-608-5.png';
import dgRight       from '../images/bulba/textures/DarkGrass/sprite-608-6.png';
import dgBottomLeft  from '../images/bulba/textures/DarkGrass/sprite-609-4.png';
import dgBottom      from '../images/bulba/textures/DarkGrass/sprite-609-5.png';
import dgBottomRight from '../images/bulba/textures/DarkGrass/sprite-609-6.png';

const ROWS_DESKTOP = 8;
const ROWS_MOBILE  = 5;
const PICKER_MULT = 1;

const DIR_TO_SPRITE = {
  ArrowDown:  0,
  ArrowLeft:  1,
  ArrowRight: 2,
  ArrowUp:    3,
};

const MOVES = {
  ArrowUp:    { row: -1, col:  0 },
  ArrowDown:  { row:  1, col:  0 },
  ArrowLeft:  { row:  0, col: -1 },
  ArrowRight: { row:  0, col:  1 },
};

// Dynamically load all sprites from the bulba folder (mirrors PokeRender pattern)
const spriteCtx = require.context('../images/bulba/Normal', false);
const ALL_SPRITES = spriteCtx.keys()
  .filter(k => /\/\d{3}\.png$/.test(k))   // only 3-digit base forms, no variants
  .sort()
  .map(key => {
    const mod = spriteCtx(key);
    const img = mod && typeof mod === 'object' && mod.default ? mod.default : mod;
    return { id: key.replace('./', '').replace('.png', ''), img };
  });

const shinyCtx = require.context('../images/bulba/Shiny', false);
const ALL_SHINY_SPRITES = shinyCtx.keys()
  .filter(k => /\/\d{3}s\.png$/.test(k))  // only 3-digit base shiny forms
  .sort()
  .map(key => {
    const mod = shinyCtx(key);
    const img = mod && typeof mod === 'object' && mod.default ? mod.default : mod;
    return { id: key.replace('./', '').replace('s.png', ''), img };
  });

const DEFAULT_IMG = redSprite;
const INITIAL_MOVE_DELAY = 500;
const MIN_MOVE_DELAY = 150;
const SPEED_STEP = 25;
const INITIAL_BODY = [
  { row: 3, col: 0, direction: 'ArrowRight', sprite: DEFAULT_IMG },
];

const getRandomSprite = () => {
  const pool = ALL_SPRITES.filter(s => !EXTRA_LARGE_DEX.has(s.id));
  return pool[Math.floor(Math.random() * pool.length)];
};

const getEkansSprite = () => {
  const normal = ALL_SPRITES.find(s => s.id === '023');
  const shiny  = ALL_SHINY_SPRITES.find(s => s.id === '023');
  const useShiny = shiny && Math.random() < 0.05;
  return (useShiny ? shiny : normal) ?? getRandomSprite();
};

const createCollectible = (rows, cols, body, ekansOnly = false) => {
  const occupied = new Set(body.map(seg => `${seg.row},${seg.col}`));
  const emptyTiles = [];

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      if (!occupied.has(`${row},${col}`)) {
        emptyTiles.push({ row, col });
      }
    }
  }

  if (!emptyTiles.length) return null;

  const tile = emptyTiles[Math.floor(Math.random() * emptyTiles.length)];
  const sprite = ekansOnly ? getEkansSprite() : getRandomSprite();
  return {
    ...tile,
    id: sprite.id,
    sprite: sprite.img,
  };
};

// Dex numbers whose overworld sprite sheets use larger-than-standard frames
const EXTRA_LARGE_DEX = new Set(['208', '249', '250', '382', '383', '384', '486', '487']);

// Per-direction 4-frame walk cycle for Red. Coordinates are [col, row] matching
// the user-provided layout. The inbetween frame is repeated at positions 1 and 3.
// dirNum order matches DIR_TO_SPRITE: 0=Down, 1=Left, 2=Right, 3=Up
const RED_DIR_FRAMES = [
  [[2,2],[2,1],[2,3],[2,1]], // Down:  step1 → inbetween → step2 → inbetween
  [[0,1],[0,2],[0,3],[0,2]], // Left:  step1 → inbetween → step2 → inbetween
  [[1,1],[1,0],[1,2],[1,0]], // Right: step1 → inbetween → step2 → inbetween
  [[2,0],[0,0],[1,3],[0,0]], // Up:    step1 → inbetween → step2 → inbetween
];

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
}

function useAnimFrame() {
  const [count, setCount] = useState(0);
  const requestRef = useRef();
  const prevTimeRef = useRef();

  useEffect(() => {
    const animate = time => {
      if (prevTimeRef.current !== undefined) {
        const delta = time - prevTimeRef.current;
        setCount(prev => (prev + delta * 0.0035) % 4);
      }
      prevTimeRef.current = time;
      requestRef.current = requestAnimationFrame(animate);
    };
    requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef.current);
  }, []);

  return Math.floor(count);
}

export default function Game2({ dark, userData }) {
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 768;
  const ROWS = isMobile ? ROWS_MOBILE : ROWS_DESKTOP;
  const CELL_SIZE = isMobile ? 78 : 104;
  const SPRITE_MULT = isMobile ? 1.5 : 2;
  const COLS = Math.max(3, Math.floor((windowWidth - 200) / CELL_SIZE));

  const [body, setBody] = useState(INITIAL_BODY);
  const [showPicker, setShowPicker] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const [filter, setFilter] = useState('');
  const [shiny, setShiny] = useState(false);
  const [tileMode] = useState('new');
  const [ekansMode, setEkansMode] = useState(false);
  const ekansModeRef = useRef(false);
  const [muted, setMuted] = useState(false);
  const mutedRef = useRef(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [isGameOver, setIsGameOver] = useState(false);
  const [collectible, setCollectible] = useState(() => createCollectible(ROWS, COLS, INITIAL_BODY));
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardMessage, setLeaderboardMessage] = useState('');
  const [scoreSaveMessage, setScoreSaveMessage] = useState('');
  const [newHighScore, setNewHighScore] = useState(false);
  const pickerScrollRef = useRef();
  const savedScrollPos = useRef(0);
  const battleAudioRef = useRef(null);

  // Battle music — play while game is running, stop on game over or unmount
  useEffect(() => {
    if (!battleAudioRef.current) {
      const audio = new Audio(battleBgm);
      audio.loop = true;
      audio.volume = 0.5;
      battleAudioRef.current = audio;
    }
    const audio = battleAudioRef.current;
    if (hasStarted && !isGameOver && !muted) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
      if (isGameOver || !hasStarted) audio.currentTime = 0;
    }
    return () => {
      audio.pause();
      audio.currentTime = 0;
    };
  }, [hasStarted, isGameOver, muted]);

  useEffect(() => { ekansModeRef.current = ekansMode; }, [ekansMode]);
  useEffect(() => { mutedRef.current = muted; }, [muted]);

  const rowsRef = useRef(ROWS);
  const colsRef = useRef(COLS);
  useEffect(() => { rowsRef.current = ROWS; }, [ROWS]);
  useEffect(() => { colsRef.current = COLS; }, [COLS]);

  const frame = useAnimFrame();
  const moveDelay = Math.max(MIN_MOVE_DELAY, INITIAL_MOVE_DELAY - ((body.length - INITIAL_BODY.length) * SPEED_STEP));
  const username = userData?.username;

  const turn = useCallback((key) => {
    if (!MOVES[key]) return;
    if (isGameOver) return;
    setBody(prev => [
      { ...prev[0], direction: key },
      ...prev.slice(1),
    ]);
  }, [isGameOver]);

  const advance = useCallback(() => {
    setBody(prev => {
      const key = prev[0].direction;
      const delta = MOVES[key];
      if (!delta) return prev;

      const nextRow = prev[0].row + delta.row;
      const nextCol = prev[0].col + delta.col;
      if (
        nextRow < 0 ||
        nextRow >= rowsRef.current ||
        nextCol < 0 ||
        nextCol >= colsRef.current
      ) {
        setIsGameOver(true);
        setHasStarted(false);
        return prev;
      }

      const hitTail = prev.slice(1).some(seg => seg.row === nextRow && seg.col === nextCol);
      if (hitTail) {
        setIsGameOver(true);
        setHasStarted(false);
        return prev;
      }

      const newHead = {
        ...prev[0],
        row: nextRow,
        col: nextCol,
      };
      const pickedUpCollectible = collectible && collectible.row === nextRow && collectible.col === nextCol;

      const newBody = [newHead];
      for (let i = 1; i < prev.length; i++) {
        newBody.push({ ...prev[i - 1], sprite: prev[i].sprite });
      }

      if (pickedUpCollectible) {
        newBody.push({ ...prev[prev.length - 1], sprite: collectible.sprite });
        setCollectible(createCollectible(rowsRef.current, colsRef.current, newBody, ekansModeRef.current));
      }

      return newBody;
    });
  }, [collectible]);

  const handleKey = useCallback((e) => {
    if (!MOVES[e.key]) return;
    e.preventDefault();
    if (!hasStarted) return;
    turn(e.key);
  }, [hasStarted, turn]);

  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  useEffect(() => {
    if (!hasStarted || isGameOver) return undefined;
    const autoAdvance = setInterval(advance, moveDelay);
    return () => clearInterval(autoAdvance);
  }, [advance, hasStarted, isGameOver, moveDelay]);

  const retry = () => {
    setBody(INITIAL_BODY);
    setCollectible(createCollectible(rowsRef.current, colsRef.current, INITIAL_BODY, ekansMode));
    setIsGameOver(false);
    setHasStarted(true);
    setScoreSaveMessage('');
    setNewHighScore(false);
  };

  useEffect(() => {
    if (!isGameOver) return;

    let isMounted = true;

    const syncScoreAndLeaderboard = async () => {
      setLeaderboardMessage('Loading leaderboard...');
      setNewHighScore(false);

      if (username) {
        setScoreSaveMessage('Checking best score...');
        const currentScoreResult = await getGameScore(username);
        const currentBest = currentScoreResult.success ? currentScoreResult.score : 0;

        if (body.length > currentBest) {
          setScoreSaveMessage('Saving new best score...');
          const saveResult = await saveGameScore(username, body.length);
          if (isMounted) {
            setScoreSaveMessage(saveResult.success ? 'New best score saved.' : saveResult.message || 'Could not save score.');
            setNewHighScore(saveResult.success);
            if (saveResult.success && !mutedRef.current) {
              const audio = new Audio(keyItemSfx);
              audio.play().catch(() => {});
            }
          }
        } else if (isMounted) {
          setScoreSaveMessage(`Best score: ${currentBest} segment${currentBest !== 1 ? 's' : ''}`);
          if (!mutedRef.current) {
            const audio = new Audio(noGoodSfx);
            audio.volume = 0.4;
            audio.play().catch(() => {});
          }
        }
      } else {
        setScoreSaveMessage('');
        if (!mutedRef.current) {
          const audio = new Audio(noGoodSfx);
          audio.volume = 0.4;
          audio.play().catch(() => {});
        }
      }

      const leaderboardResult = await getGameLeaderboard();
      if (!isMounted) return;
      if (leaderboardResult.success) {
        setLeaderboard(leaderboardResult.leaderboard);
        setLeaderboardMessage('');
      } else {
        setLeaderboard([]);
        setLeaderboardMessage(leaderboardResult.message || 'Leaderboard unavailable.');
      }
    };

    syncScoreAndLeaderboard();

    return () => {
      isMounted = false;
    };
  }, [body.length, isGameOver, username]);

  // Play level-up sound when snake grows during active gameplay
  const prevBodyLenRef = useRef(INITIAL_BODY.length);
  useEffect(() => {
    const grew = body.length > prevBodyLenRef.current;
    prevBodyLenRef.current = body.length;
    if (!grew || !hasStarted || isGameOver || mutedRef.current) return;
    const audio = new Audio(levelUpSfx);
    audio.volume = 0.8;
    audio.play().catch(() => {});
  }, [body.length, hasStarted, isGameOver]);

  useEffect(() => {
    setBody(prev => prev.map(seg => ({
      ...seg,
      col: Math.min(seg.col, COLS - 1),
    })));
  }, [COLS]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCollectible(prev => {
      const overlapsBody = prev && body.some(seg => seg.row === prev.row && seg.col === prev.col);
      if (!prev || prev.row >= ROWS || prev.col >= COLS || overlapsBody) {
        return createCollectible(ROWS, COLS, body, ekansMode);
      }
      return prev;
    });
  }, [ROWS, COLS, body, ekansMode]);

  useEffect(() => {
    if (showPicker && pickerScrollRef.current) {
      pickerScrollRef.current.scrollTop = savedScrollPos.current;
    }
  }, [showPicker]);

  const addSegment = (sprite) =>
    setBody(prev => [...prev, { ...prev[prev.length - 1], sprite }]);

  const addRandomParty = () => {
    const usedSprites = new Set(body.map(seg => seg.sprite));
    const normalPool = ALL_SPRITES.filter(s => !EXTRA_LARGE_DEX.has(s.id) && !usedSprites.has(s.img));
    const shinyPool  = ALL_SHINY_SPRITES.filter(s => !EXTRA_LARGE_DEX.has(s.id) && !usedSprites.has(s.img));
    setBody(prev => {
      const newSegs = Array.from({ length: 20 }, () => {
        const pickShiny = shinyPool.length > 0 && Math.random() < 0.05;
        const pool = pickShiny ? shinyPool : normalPool;
        if (!pool.length) return null;
        return { ...prev[prev.length - 1], sprite: pool[Math.floor(Math.random() * pool.length)].img };
      }).filter(Boolean);
      return [...prev, ...newSegs];
    });
  };

  const cellMap = new Map();
  body.forEach((seg, i) => {
    const key = `${seg.row},${seg.col}`;
    if (!cellMap.has(key)) cellMap.set(key, i);
  });

  const borderColor = dark ? '#555' : '#aaa';
  const bgColor     = dark ? '#1e1e1e' : '#f5f5f5';
  const cellBg      = dark ? '#2a2a2a' : '#fff';
  const textColor   = dark ? '#ccc' : '#444';

  const renderSegment = (f, seg, dirNum) => {
    const rf = 3 - f;
    if (seg.sprite === redSprite) {
      const [col, row] = RED_DIR_FRAMES[dirNum][rf];
      return SpriteFrame(redSprite, row, col, SPRITE_MULT, 3, 4);
    }
    return SpriteFourStep(rf, seg.sprite, 0, dirNum, SPRITE_MULT);
  };

  const trimmed = filter.trim();
  const spritePool = shiny ? ALL_SHINY_SPRITES : ALL_SPRITES;
  const filtered = trimmed
    ? spritePool.filter(s => s.id.includes(trimmed))
    : spritePool;

  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <h2>PokéSnake</h2>
      <h4 style={{ color: dark ? '#fffdfd' : '#555', marginBottom: '1rem'}}>
        Use arrow keys or the D-pad to steer. Press Start to begin automatic movement.
      </h4>
      <style>{`
        @keyframes sparklePulse {
          0% {
            transform: scale(1);
            letter-spacing: 0;
            filter: brightness(1);
          }
          100% {
            transform: scale(1.045);
            letter-spacing: 0.04em;
            filter: brightness(1.25);
          }
        }
      `}</style>

      <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'center', gap: '1rem', alignItems: 'center' }}>
        <span style={{ color: '#fffdfd', minWidth: 80, fontSize: "2rem" }}>
          {body.length} segment{body.length !== 1 ? 's' : ''}
        </span>
        <button
          onClick={() => setMuted(m => !m)}
          title={muted ? 'Unmute' : 'Mute'}
          style={{
            width: 40, height: 40, fontSize: '1.2rem',
            borderRadius: 20,
            border: `2px solid ${borderColor}`,
            background: cellBg,
            color: dark ? '#fff' : '#333',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {muted ? '🔇' : '🔊'}
        </button>
      </div>

      <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'center' }}>
        <button
          onClick={() => {
            const next = !ekansMode;
            setEkansMode(next);
            ekansModeRef.current = next;
            setCollectible(createCollectible(rowsRef.current, colsRef.current, body, next));
          }}
          style={{
            padding: '0 16px', height: 40, fontSize: '0.85rem', fontWeight: 'bold',
            borderRadius: 20,
            border: `2px solid ${ekansMode ? '#cc2200' : borderColor}`,
            background: ekansMode
              ? (dark ? 'rgba(180,30,0,0.35)' : 'rgba(220,60,30,0.15)')
              : cellBg,
            color: ekansMode ? (dark ? '#ff7755' : '#cc2200') : (dark ? '#fff' : '#333'),
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            transition: 'all 0.15s',
          }}
        >
          🐍 All Ekans Mode{ekansMode ? ' ✓' : ''}
        </button>
      </div>

      {username === 'Andrew_Dilks' && (
        <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'center' }}>
          <button
            onClick={addRandomParty}
            style={{
              padding: '0 14px', height: 40, fontSize: '0.85rem', fontWeight: 'bold',
              borderRadius: 20, border: `2px solid ${borderColor}`,
              background: cellBg, color: dark ? '#fff' : '#333',
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            🎲 Random ×20
          </button>
        </div>
      )}

      {/* Sprite picker modal */}
      {showPicker && (
        <div
          onClick={() => setShowPicker(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 200,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: cellBg,
              border: `2px solid ${borderColor}`,
              borderRadius: 10,
              padding: '1.25rem',
              width: 420,
              maxWidth: '92vw',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '80vh',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexShrink: 0 }}>
              <h3 style={{ color: textColor, margin: 0 }}>
                Choose a Pokémon
              </h3>
              <button
                onClick={() => setShiny(s => !s)}
                style={{
                  padding: '4px 10px',
                  borderRadius: 6,
                  border: `2px solid ${shiny ? '#f5c518' : borderColor}`,
                  background: shiny ? (dark ? '#3a3000' : '#fffbe6') : 'transparent',
                  color: shiny ? '#f5c518' : textColor,
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  transition: 'all 0.15s',
                }}
              >
                ★ Shiny
              </button>
            </div>

            <input
              autoFocus
              placeholder="Filter by dex # (e.g. 025)"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              style={{
                marginBottom: '0.75rem',
                padding: '6px 10px',
                borderRadius: 6,
                border: `1px solid ${borderColor}`,
                background: dark ? '#1a1a1a' : '#fff',
                color: textColor,
                fontSize: '1rem',
                flexShrink: 0,
                outline: 'none',
              }}
            />

            <div ref={pickerScrollRef} style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
              gap: 4,
              overflowY: 'auto',
              overflowX: 'hidden',
              paddingRight: 4,
            }}>
              {filtered.map(s => (
                <div
                  key={s.id}
                  title={`#${s.id}`}
                  onMouseEnter={() => setHoveredId(s.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onClick={() => { savedScrollPos.current = pickerScrollRef.current?.scrollTop ?? 0; addSegment(s.img); setShowPicker(false); setHoveredId(null); }}
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '4px 2px',
                    borderRadius: 6,
                    border: `2px solid ${hoveredId === s.id ? (dark ? '#88aaff' : '#4466cc') : 'transparent'}`,
                    background: hoveredId === s.id ? (dark ? '#333' : '#eef') : 'transparent',
                    transition: 'border-color 0.1s, background 0.1s',
                  }}
                >
                  <div style={{ transform: 'translateY(-6px)', pointerEvents: 'none' }}>
                    {SpriteFourStep(3 - frame, s.img, 0, 0, PICKER_MULT)}
                  </div>
                  <span style={{
                    fontSize: '0.55rem',
                    color: textColor,
                    textAlign: 'center',
                    marginTop: -4,
                    lineHeight: 1.2,
                  }}>
                    #{s.id}
                  </span>
                </div>
              ))}
              {filtered.length === 0 && (
                <span style={{ color: textColor, gridColumn: '1/-1', padding: '1rem', fontSize: '0.85rem' }}>
                  No sprites match "{filter}"
                </span>
              )}
            </div>
          </div>
        </div>
      )}


      <div style={{ position: 'relative', display: 'inline-block' }}>
        <div style={{ display: 'inline-block', border: `2px solid ${borderColor}`, background: bgColor, borderRadius: '6px', padding: '3px', overflow: 'hidden' }}>
          {Array.from({ length: ROWS }, (_, r) => (
            <div key={r} style={{ display: 'flex' }}>
              {Array.from({ length: COLS }, (_, c) => {
                const segIndex = cellMap.get(`${r},${c}`);
                const hasSeg = segIndex !== undefined;
                const seg = hasSeg ? body[segIndex] : null;
                const hasCollectible = collectible && collectible.row === r && collectible.col === c;
                const dirNum = hasSeg ? (DIR_TO_SPRITE[seg.direction] ?? 2) : 2;
                const isHead = segIndex === 0;
                const isTopEdge    = r === 0;
                const isBottomEdge = r === ROWS - 1;
                const isLeftEdge   = c === 0;
                const isRightEdge  = c === COLS - 1;
                const tileSet = tileMode === 'new'
                  ? { tl: dark ? dgTopLeft : lgTopLeft, tc: dark ? dgTop : lgTop, tr: dark ? dgTopRight : lgTopRight,
                      ml: dark ? dgLeft : lgLeft,        mc: dark ? dgCenter : lgCenter, mr: dark ? dgRight : lgRight,
                      bl: dark ? dgBottomLeft : lgBottomLeft, bc: dark ? dgBottom : lgBottom, br: dark ? dgBottomRight : lgBottomRight }
                  : tileMode === 'original'
                  ? { tl: ogTopLeft, tc: ogTop, tr: ogTopRight, ml: ogLeft, mc: ogCenter, mr: ogRight, bl: ogBottomLeft, bc: ogBottom, br: ogBottomRight }
                  : null;

                let tileBg = grassFlat;
                if (tileSet) {
                  tileBg = tileSet.mc;
                  if      (isTopEdge    && isLeftEdge)                   tileBg = tileSet.tl;
                  else if (isTopEdge    && isRightEdge)                  tileBg = tileSet.tr;
                  else if (isBottomEdge && isLeftEdge)                   tileBg = tileSet.bl;
                  else if (isBottomEdge && isRightEdge)                  tileBg = tileSet.br;
                  else if (isTopEdge    && !isLeftEdge && !isRightEdge)  tileBg = tileSet.tc;
                  else if (isBottomEdge && !isLeftEdge && !isRightEdge)  tileBg = tileSet.bc;
                  else if (isLeftEdge   && !isTopEdge  && !isBottomEdge) tileBg = tileSet.ml;
                  else if (isRightEdge  && !isTopEdge  && !isBottomEdge) tileBg = tileSet.mr;
                }
                return (
                  <div
                    key={c}
                    style={{
                      width: CELL_SIZE,
                      height: CELL_SIZE,
                      // border: `1px solid ${borderColor}`,
                      backgroundImage: `url(${tileBg})`,
                      backgroundSize: 'cover',
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      opacity: hasSeg && !isHead ? 1 : 1,
                    }}
                  >
                    <div style={{ transform: 'translateY(8px)' }}>
                      {hasSeg && renderSegment(frame, seg, dirNum)}
                      {!hasSeg && hasCollectible && SpriteFourStep(3 - frame, collectible.sprite, 0, 0, SPRITE_MULT)}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {(!hasStarted || isGameOver) && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: dark ? 'rgba(0, 0, 0, 0.42)' : 'rgba(255, 255, 255, 0.42)',
              backdropFilter: 'blur(1px)',
              zIndex: 1,
            }}
          >
            <div
              style={{
                width: isGameOver ? 'min(92%, 460px)' : 'auto',
                maxHeight: '92%',
                overflowY: 'auto',
                textAlign: 'center',
                padding: isGameOver ? '1.25rem 1.4rem' : 0,
                borderRadius: isGameOver ? 12 : 0,
                border: isGameOver ? `2px solid ${borderColor}` : 'none',
                background: isGameOver
                  ? (dark ? 'rgba(12, 18, 24, 0.9)' : 'rgba(255, 255, 255, 0.92)')
                  : 'transparent',
                boxShadow: isGameOver ? '0 12px 32px rgba(0,0,0,0.35)' : 'none',
              }}
            >
              {isGameOver && (
                <>
                  {newHighScore && (
                    <div
                      style={{
                        color: dark ? '#80ff99' : '#1a7a2e',
                        fontSize: '1.65rem',
                        fontWeight: 900,
                        marginBottom: '0.55rem',
                        animation: 'sparklePulse 0.9s ease-in-out infinite alternate',
                        textShadow: dark
                          ? '0 0 6px #fff, 0 0 14px #4dff80, 0 0 24px #00cc44'
                          : '0 0 5px #fff, 0 0 13px #33cc55, 0 0 20px #009933',
                      }}
                    >
                      Congratulations! New High Score!
                    </div>
                  )}
                  <div
                    style={{
                      color: dark ? '#fff' : '#222',
                      fontSize: '1.75rem',
                      fontWeight: 'bold',
                      marginBottom: '0.4rem',
                      textShadow: dark ? '0 2px 6px rgba(0,0,0,0.55)' : '0 2px 6px rgba(255,255,255,0.55)',
                    }}
                  >
                    Game Over
                  </div>
                  <div
                    style={{
                      color: dark ? '#fff' : '#222',
                      fontSize: '1.15rem',
                      fontWeight: 'bold',
                      marginBottom: '0.65rem',
                      textShadow: dark ? '0 2px 6px rgba(0,0,0,0.55)' : '0 2px 6px rgba(255,255,255,0.55)',
                    }}
                  >
                    Score: {body.length} segment{body.length !== 1 ? 's' : ''}
                  </div>
                  {!userData ? (
                    <Link
                      to="/login"
                      style={{
                        display: 'inline-block',
                        color: dark ? '#d9ecff' : '#123c7a',
                        fontSize: '1.05rem',
                        fontWeight: 'bold',
                        marginBottom: '0.9rem',
                        textDecoration: 'underline',
                      }}
                    >
                      Login to save your score
                    </Link>
                  ) : (
                    <div
                      style={{
                        color: dark ? '#fff' : '#222',
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        marginBottom: '0.9rem',
                        textShadow: dark ? '0 2px 6px rgba(0,0,0,0.55)' : '0 2px 6px rgba(255,255,255,0.55)',
                      }}
                    >
                      {scoreSaveMessage}
                    </div>
                  )}
                  <div
                    style={{
                      minWidth: 260,
                      maxWidth: 380,
                      margin: '0 auto 1rem',
                      padding: '0.85rem',
                      borderRadius: 8,
                      background: dark ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.72)',
                      border: `1px solid ${borderColor}`,
                      color: dark ? '#fff' : '#222',
                      textAlign: 'left',
                    }}
                  >
                    <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', textAlign: 'center', fontSize: '1.05rem' }}>
                      Leaderboard
                    </div>
                    {leaderboardMessage ? (
                      <div style={{ fontSize: '0.82rem', textAlign: 'center' }}>
                        {leaderboardMessage}
                      </div>
                    ) : leaderboard.length > 0 ? (
                      leaderboard.slice(0, 10).map((entry, index) => {
                        const entryName = entry.username || entry.user_id || 'Player';
                        const isCurrentUser = username && entryName === username;
                        return (
                          <div
                            key={`${entry.user_id || entry.username || index}-${index}`}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: '1rem',
                              fontSize: '0.95rem',
                              lineHeight: 1.35,
                              borderRadius: isCurrentUser ? 5 : 0,
                              padding: isCurrentUser ? '1px 5px' : '1px 5px',
                              margin: isCurrentUser ? '1px -5px' : '1px -5px',
                              background: isCurrentUser
                                ? (dark ? 'rgba(255, 215, 0, 0.18)' : 'rgba(255, 193, 7, 0.22)')
                                : 'transparent',
                              fontWeight: isCurrentUser ? 'bold' : 'normal',
                              color: isCurrentUser
                                ? (dark ? '#ffe066' : '#7a5500')
                                : 'inherit',
                            }}
                          >
                            <span>
                              {index + 1}. {entryName}
                              {isCurrentUser && (
                                <span style={{ marginLeft: 5, fontSize: '0.75rem', opacity: 0.85 }}>
                                  (you)
                                </span>
                              )}
                            </span>
                            <span>{entry.score}</span>
                          </div>
                        );
                      })
                    ) : (
                      <div style={{ fontSize: '0.82rem', textAlign: 'center' }}>
                        No scores yet.
                      </div>
                    )}
                  </div>
                </>
              )}
              <button
                type="button"
                onClick={isGameOver ? retry : () => setHasStarted(true)}
                style={{
                  padding: '0.65rem 1.5rem',
                  minWidth: isGameOver ? 120 : 'auto',
                  borderRadius: 8,
                  border: `2px solid ${borderColor}`,
                  background: cellBg,
                  color: dark ? '#fff' : '#333',
                  cursor: 'pointer',
                  fontSize: isGameOver ? '1.2rem' : '1.1rem',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                }}
              >
                {isGameOver ? 'Retry' : 'Start'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* D-pad */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(3, ${CELL_SIZE / 2}px)`,
        gridTemplateRows: `repeat(3, ${CELL_SIZE / 2}px)`,
        gap: 4,
        marginTop: '1.5rem',
        justifyContent: 'center',
      }}>
        {[
          { key: null },
          { key: 'ArrowUp',    label: '▲' },
          { key: null },
          { key: 'ArrowLeft',  label: '◀' },
          { key: null },
          { key: 'ArrowRight', label: '▶' },
          { key: null },
          { key: 'ArrowDown',  label: '▼' },
          { key: null },
        ].map((btn, i) => (
          btn.key ? (
            <button
              key={i}
              onPointerDown={e => { e.preventDefault(); if (hasStarted) turn(btn.key); }}
              style={{
                width: CELL_SIZE / 2, height: CELL_SIZE / 2,
                fontSize: '1.2rem',
                border: `2px solid ${borderColor}`,
                borderRadius: 8,
                background: cellBg,
                color: dark ? '#fff' : '#333',
                cursor: 'pointer',
                userSelect: 'none',
                touchAction: 'none',
              }}
            >
              {btn.label}
            </button>
          ) : (
            <div key={i} />
          )
        ))}
      </div>

    </div>
  );
}
